import React, { useEffect, useRef, useState, useCallback } from "react";
import { Script } from "components/ExtendedContent/ExtendedContentContainer/layout";
import { scriptLoader } from "util/scriptLoader";
import { useStore, StoreTypes } from "context";
import { parseChineseOnly } from "util/zhuyin";
import * as types from "constants/actionTypes";
import { PAGE_SIZE } from "./utils/constants";
import { splitIntoPagesString } from "./utils/pageSplitter";
import TextStructureHandler from "./TextStructureHandler";
import ContentStructureHandler from "./ContentStructureHandler";
import { EventBusType, EventBus } from 'events/EventBus';
import { CanvasEvent } from 'events/EventTypes';
import { LayoutType } from "components/ExtendedContent/ExtendedContentContainer/layout";
import { SplitType } from "constants/splitType";

const StaticHtmlComponent = ({ html, extendedResourceStyle }) => {
  const [{ bookId, books }] = useStore(StoreTypes.books);
  const [{ shouldSplited, splitType }] = useStore(StoreTypes.extendedContent);
  const book = books.find((info) => info.bookId === bookId);
  const highGradeCheck = book.display_name.includes("國中國文");
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [{ currentPage }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);
  const [pagedHtml, setPagedHtml] = useState(null);
  
  // 重置內容縮放到預設大小
  const resetContentScale = useCallback(() => {
    if (!contentRef.current) return;
    const content = contentRef.current;
    content.style.fontSize = '';
    content.style.transform = 'none';
    content.style.transformOrigin = '';
    content.style.height = '';
    content.style.width = '100%';
  }, []);

  // 調整內容縮放，使內容能顯示在可見範圍內
  const adjustContentScale = useCallback(() => {
    if (!containerRef.current || !contentRef.current) return;

    const container = containerRef.current;
    const content = contentRef.current;

    // 重置所有樣式，確保測量準確
    content.style.fontSize = '';
    content.style.transform = 'none';
    content.style.transformOrigin = '';
    content.style.height = '';
    content.style.width = '100%';

    // 強制重排一次
    void container.offsetHeight;

    // 計算容器和內容高度
    const containerHeight = container.clientHeight;
    let contentHeight = content.scrollHeight;
    
    // 如果內容高度超過容器高度，使用二分搜索找到最佳字體大小
    if (contentHeight > containerHeight && contentHeight > 0) {
      // 取得原始字體大小
      const computedStyle = window.getComputedStyle(content);
      const originalFontSize = parseFloat(computedStyle.fontSize);
      
      // 二分搜索參數
      let minFontSize = originalFontSize * 0.3; // 最小 30%
      let maxFontSize = originalFontSize; // 最大 100%
      const tolerance = 5; // 容許 5px 誤差
      const maxIterations = 20;
      
      let bestFontSize = minFontSize;
      let iteration = 0;
      
      while (maxFontSize - minFontSize > 0.5 && iteration < maxIterations) {
        const midFontSize = (minFontSize + maxFontSize) / 2;
        
        // 應用測試字體大小
        content.style.fontSize = `${midFontSize}px`;
        
        // 強制重排
        void content.offsetHeight;
        
        // 測量調整後的實際高度
        const actualHeight = content.scrollHeight;
        
        if (actualHeight <= containerHeight + tolerance) {
          // 高度合適，記錄這個字體大小，嘗試更大的值
          bestFontSize = midFontSize;
          minFontSize = midFontSize;
        } else {
          // 高度太大，需要更小的字體
          maxFontSize = midFontSize;
        }
        
        iteration++;
      }
      
      // 應用最佳字體大小
      content.style.fontSize = `${bestFontSize}px`;
      
    } else {
      // 內容未超過容器，保持預設字體大小
      content.style.fontSize = '';
    }
  }, []);
  
  // 筆記標記相關的狀態
  // const [{ currentPageIndex }, setState] = useSetState({
  //   currentPageIndex: 0
  // });

  // 將腳本載入移到分頁完成後
  useEffect(() => {
    if (pagedHtml && contentRef.current && html) {
      // 等待 DOM 完全渲染後再載入腳本
      const timer = setTimeout(() => {
        scriptLoader(Script[extendedResourceStyle]);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [pagedHtml, extendedResourceStyle, html]);

  // 同步 currentPage 和 currentPageIndex（當頁面切換時觸發筆記標記）
  useEffect(() => {
    if (!shouldSplited) return;
    if (currentPage >= 0) {
      // 先保存當前頁面的筆記
      EventBus.emit({
        eventBusType: EventBusType.ExtendedContent,
        event: CanvasEvent.SplitedPageSaveCanvasEvent,
        payload: { currentSplitedPageIndex: currentPage }
      });
      
      // 然後更新到新頁面
      // setState({ currentPageIndex: currentPage });
    }
  }, [currentPage, currentPage, shouldSplited]);

  // 筆記標記：當 currentPageIndex 變化時，載入對應頁面的筆記
  useEffect(() => {
    if (!shouldSplited) return;
    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: CanvasEvent.SplitedPageImportJSONEvent,
      payload: { currentSplitedPageIndex: currentPage }
    });

    if (splitType === SplitType.MANUAL_SPLIT) {
      const page = (currentPage + 1).toString();
      setPagedHtml(html[page]);
      return;
    }
  }, [currentPage, shouldSplited, splitType]);

  // 分頁處理
  useEffect(() => {
    if (!shouldSplited && html && containerRef.current) {
      setPagedHtml(html);
      return;
    }
    if (!html || !containerRef.current || !shouldSplited) return;

    if (splitType === SplitType.NO_SPLIT) {
      setPagedHtml(html);
      return;
    }

    if (splitType === SplitType.MANUAL_SPLIT) {
      setPagedHtml(html['1']);

      const totalPages = Object.keys(html).length;
      // 更新 store 中的總頁數
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages },
      });
      return;
    }

    resetContentScale();
    
    const measureContainer = containerRef.current;
    const originalHtml = highGradeCheck ? parseChineseOnly(html, "pirls") : html;

    // 等待渲染完成後測量
    requestAnimationFrame(() => {
      const computedStyle = window.getComputedStyle(measureContainer);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 
                        parseFloat(computedStyle.fontSize) * 1.5;
      
      // 生成分頁後的 HTML 字串
      const { htmlString, totalPages } = splitIntoPagesString(
        originalHtml, 
        lineHeight, 
        PAGE_SIZE,
        measureContainer,
        extendedResourceStyle
      );
      
      // 設置分頁後的 HTML
      setPagedHtml(htmlString);
      
      // 更新 store 中的總頁數
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages },
      });
      
    });
  }, [html, highGradeCheck, extendedContentDispatch, extendedResourceStyle, shouldSplited, splitType, resetContentScale]);

  // 當頁面切換、內容變化時調整縮放
  useEffect(() => {
    if (pagedHtml && containerRef.current && contentRef.current && shouldSplited) {
      requestAnimationFrame(() => {
        adjustContentScale();
      });
    }
  }, [pagedHtml, currentPage, adjustContentScale, shouldSplited]);

  return (
    <div ref={containerRef}>
      {pagedHtml && (
        <>
          <div
            ref={contentRef}
            dangerouslySetInnerHTML={{
              __html: pagedHtml,
            }}
          />
          {shouldSplited && splitType === SplitType.AUTO_SPLIT && (extendedResourceStyle === LayoutType.PlainText || extendedResourceStyle === LayoutType.Topic_108_2) && (
            <TextStructureHandler
              pagedHtml={pagedHtml}
              currentPage={currentPage}
              currentPageIndex={currentPage}
              contentRef={contentRef}
            />
          )}
          {shouldSplited && splitType === SplitType.AUTO_SPLIT && (extendedResourceStyle === LayoutType.Simple_108_2 || extendedResourceStyle === LayoutType.Fill_108_2) && (
            <ContentStructureHandler
              pagedHtml={pagedHtml}
              currentPage={currentPage}
              currentPageIndex={currentPage}
              contentRef={contentRef}
            />
          )}
        </>
      )}
    </div>
  );
};

export default StaticHtmlComponent;
